'use client';

import { useCallback, useMemo } from 'react';
import { useDisconnect as useDisconnectWagmi, useAccount as useWagmiAccount } from 'wagmi';
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import { SOLANA_CHAIN_ID } from 'mode-shared/context/SolanaContext/SolanaWalletProvider';
import { Button, CopyButton, SwitchIcon, AccountIcon } from 'mode-shared/components';
import { formatWalletAddress } from 'mode-shared/utils/format';
interface WalletInterface {
  iconSrc?: string;
  accountAddress?: string;
  name?: string;
  type?: 'ethereum' | 'solana';
}

export const BridgeWallet = ({ selectedChainId }: { selectedChainId?: number }) => {
  // EVM wallets
  const { address: wagmiAccountAddress, isConnected: isWagmiConnected, connector: wagmiWallet } = useWagmiAccount();
  const { disconnect: disconnectWagmi } = useDisconnectWagmi();
  // SOLANA wallets
  const {
    publicKey: solanaPublicKey,
    connected: isSolanaConnected,
    disconnect: disconnectSolana,
    wallet: solanaWallet,
  } = useSolanaWallet();

  const wallet = useMemo<WalletInterface>(() => {
    if (selectedChainId === SOLANA_CHAIN_ID) {
      return isSolanaConnected
        ? {
            iconSrc: solanaWallet?.adapter.icon,
            accountAddress: solanaPublicKey?.toString(),
            name: solanaWallet?.adapter.name,
            type: 'solana',
          }
        : {};
    }

    return isWagmiConnected
      ? {
          iconSrc: wagmiWallet?.icon,
          accountAddress: wagmiAccountAddress,
          name: wagmiWallet?.name,
          type: 'ethereum',
        }
      : {};
  }, [wagmiWallet, solanaWallet, isSolanaConnected, selectedChainId]);

  const accountDisplayName = formatWalletAddress(wallet.accountAddress, 5, 5);

  const disconnectWallet = useCallback(() => {
    if (selectedChainId === SOLANA_CHAIN_ID && isSolanaConnected) {
      disconnectSolana();
    } else if (isWagmiConnected) {
      disconnectWagmi();
    }
  }, [isWagmiConnected, isSolanaConnected, disconnectWagmi, disconnectSolana, selectedChainId]);

  if (!wallet.accountAddress) return null;

  return (
    <div className="text-neutral-100 flex justify-between items-center bg-neutral-950 rounded-xl py-5 px-6">
      <div className="flex items-center gap-x-2">
        <AccountIcon walletIconSrc={wallet.iconSrc} size={32} />
        <div>
          <p className="flex items-center gap-x-1 text-sm">
            {accountDisplayName}
            <CopyButton size="sm" copyValue={wallet.accountAddress} />
            <span className="text-[10px] capitalize leading-none text-white p-1 bg-neutral-900 rounded-sm">
              {wallet.type}
            </span>
          </p>
          {wallet.name && <p className="text-xs">{wallet.name}</p>}
        </div>
      </div>
      <Button onClick={disconnectWallet} className="rounded-lg px-2" variant="secondary">
        <SwitchIcon />
      </Button>
    </div>
  );
};
